import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import {
  Home, Award, BookOpen, Clock, CalendarCheck,
  FileText, MessageSquare, CheckCircle,
  AlertCircle, Trophy,
  Sparkles, ChevronRight, BookMarked, Play, Upload,
  User, Calendar, MapPin
} from 'lucide-react';
import ComposerOfTheDay from '../components/ComposerOfTheDay';

const getGamificationLevel = (puntos = 0) => {
  if (puntos >= 500) {
    return { level: 6, title: 'Guardián del Patrimonio', min: 500, next: 1000, color: 'from-amber-400 to-yellow-600' };
  }
  if (puntos >= 250) {
    return { level: 5, title: 'Curador de la Memoria Sonora', min: 250, next: 500, color: 'from-purple-400 to-pink-600' };
  }
  if (puntos >= 100) {
    return { level: 4, title: 'Investigador Musical', min: 100, next: 250, color: 'from-indigo-400 to-purple-600' };
  }
  if (puntos >= 50) {
    return { level: 3, title: 'Colaborador Avanzado', min: 50, next: 100, color: 'from-blue-400 to-indigo-600' };
  }
  if (puntos >= 20) {
    return { level: 2, title: 'Colaborador Activo', min: 20, next: 50, color: 'from-emerald-400 to-teal-600' };
  }
  if (puntos >= 1) {
    return { level: 1, title: 'Colaborador Inicial', min: 1, next: 20, color: 'from-teal-400 to-emerald-500' };
  }
  return { level: 0, title: 'Principiante Musical', min: 0, next: 10, color: 'from-slate-400 to-slate-600' };
};

const AlumnoDashboardPage = () => {
  const navigate = useNavigate();
  const [alumno, setAlumno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null);

  // Estados para estudiantes de cátedras
  const [catedras, setCatedras] = useState([]);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [tareasRecientesCalificadas, setTareasRecientesCalificadas] = useState([]);
  const [evaluacionesPendientes, setEvaluacionesPendientes] = useState([]);
  const [publicaciones, setPublicaciones] = useState([]);
  const [randomComposer, setRandomComposer] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleComposerClick = (composerId) => {
    navigate(`/composers/${composerId}`);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const allComposers = await api.getComposers().catch(() => ({ data: [] }));
      const composersList = Array.isArray(allComposers.data) ? allComposers.data : (allComposers.data?.data || []);
      if (composersList.length > 0) {
        const randomIndex = Math.floor(Math.random() * composersList.length);
        setRandomComposer(composersList[randomIndex]);
      }

      // Fetch datos del alumno/aportante
      const alumnoResponse = await api.getAlumnoMe();
      const userData = alumnoResponse.data;
      setAlumno(userData);

      // Fetch cátedras inscritas
      const catedrasResponse = await api.getStudentCatedras().catch(() => ({ data: [] }));
      const catedrasData = catedrasResponse.data || [];
      setCatedras(catedrasData);

      const hasCatedras = catedrasData.length > 0;
      setUserType(hasCatedras ? 'student' : 'new');

      if (hasCatedras) {
        // Fetch tareas
        const tareasResponse = await api.getAlumnoTareas().catch(() => ({ data: [] }));
        const allTareas = tareasResponse.data || [];
        const pendientes = allTareas
          .filter(t => t.estado === 'ASIGNADA' || t.estado === 'PENDIENTE' || (t.estado === 'VENCIDA' && (!t.submission_path || t.submission_path.length === 0)))
          .sort((a, b) => {
            const dateA = a.TareaMaestra?.fecha_entrega ? new Date(a.TareaMaestra.fecha_entrega) : new Date(8640000000000000);
            const dateB = b.TareaMaestra?.fecha_entrega ? new Date(b.TareaMaestra.fecha_entrega) : new Date(8640000000000000);
            return dateA - dateB;
          });
        setTareasPendientes(pendientes);

        const calificadas = allTareas
          .filter(t => t.estado === 'CALIFICADA')
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          .slice(0, 3);
        setTareasRecientesCalificadas(calificadas);

        // Fetch evaluaciones
        const evalsResponse = await api.getMyEvaluations().catch(() => ({ data: [] }));
        const evPendientes = (evalsResponse.data || []).filter(e => !e.realizada);
        setEvaluacionesPendientes(evPendientes);

        // Fetch publicaciones de todas las cátedras
        const allPubs = [];
        for (const inscripcion of catedrasData) {
          try {
            const cat = inscripcion.catedra || inscripcion.Catedra;
            const cId = inscripcion.catedraId || cat?.id;
            if (cId) {
              const pubResponse = await api.getPublicaciones(cId);
              const pubsWithCat = (pubResponse.data || []).slice(0, 3).map(p => ({
                ...p,
                catedraNombre: cat?.nombre || 'Cátedra',
                catedraId: cId
              }));
              allPubs.push(...pubsWithCat);
            }
          } catch (err) {
            console.warn('Error fetching publications:', err);
          }
        }
        setPublicaciones(
          allPubs
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
        );
      }
    } catch (err) {
      console.error('Error al cargar dashboard:', err);
      toast.error('Error al cargar los datos del dashboard');

      if (err.response?.status === 401) {
        window.location.href = '/alumno/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const getDeadlineBadge = (fechaEntrega, estado) => {
    if (estado === 'VENCIDA') {
      return { text: 'Vencida', classes: 'bg-red-500/20 text-red-300 border-red-500/30' };
    }
    if (!fechaEntrega) {
      return { text: 'Sin fecha límite', classes: 'bg-slate-700/50 text-slate-300 border-slate-600/50' };
    }
    const now = new Date();
    const due = new Date(fechaEntrega);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: 'Plazo vencido', classes: 'bg-red-500/20 text-red-300 border-red-500/30' };
    }
    if (diffDays <= 2) {
      return { text: diffDays === 0 ? '¡Vence hoy!' : `Vence en ${diffDays} día(s)`, classes: 'bg-amber-500/20 text-amber-300 border-amber-500/30' };
    }
    return { text: `Vence ${due.toLocaleDateString()}`, classes: 'bg-blue-500/20 text-blue-300 border-blue-500/30' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-purple-600/30 rounded-full animate-spin border-t-purple-500"></div>
            <div className="absolute inset-0 border-4 border-transparent rounded-full animate-ping border-t-purple-400"></div>
          </div>
          <p className="text-slate-300 text-lg font-medium">Cargando tu centro de control...</p>
        </div>
      </div>
    );
  }

  if (!alumno) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Error al cargar datos</h2>
          <p className="text-slate-400 mb-6">No se encontraron datos de usuario</p>
          <button
            onClick={() => window.location.href = '/alumno/login'}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Volver al Login
          </button>
        </div>
      </div>
    );
  }

  const totalPuntos = alumno.totalPuntos || 0;
  const levelInfo = getGamificationLevel(totalPuntos);
  const progressPercent = Math.min(
    100,
    Math.max(5, Math.round(((totalPuntos - levelInfo.min) / Math.max(1, levelInfo.next - levelInfo.min)) * 100))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Grid Superior: Bienvenida + Nivel de Gamificación + Compositor del Día (100% Responsivo) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Bienvenida y Progreso XP */}
          <div className={`${randomComposer ? 'lg:col-span-8' : 'lg:col-span-12'} bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8 flex flex-col justify-between`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold mb-3">
                  <Sparkles size={14} />
                  {userType === 'student' ? 'Portal del Estudiante Activo' : 'Nuevo Explorador Musical'}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">
                  ¡Hola, {alumno.nombre} {alumno.apellido || ''}!
                </h1>
                <p className="text-slate-400 mt-1">
                  {alumno.instrumento ? `Instrumento principal: ${alumno.instrumento}` : 'Gestiona tus cátedras, entregas y progreso académico'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/alumno/profile/${alumno.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-600/50 text-sm font-medium transition-all"
                >
                  <User size={16} className="text-purple-400" />
                  Mi Perfil
                </Link>
                <Link
                  to="/gamification"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium shadow-lg shadow-purple-900/30 transition-all"
                >
                  <Trophy size={16} />
                  Ver Ranking
                </Link>
              </div>
            </div>

            {/* Barra de Nivel y Puntos XP */}
            <div className="bg-slate-800/50 rounded-xl p-4 sm:p-5 border border-slate-700/50">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${levelInfo.color} flex items-center justify-center text-white font-bold shadow-md`}>
                    <Award size={22} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider font-semibold text-purple-300">
                        Nivel {levelInfo.level}
                      </span>
                      <span className="text-slate-500">•</span>
                      <span className="text-white font-bold">{levelInfo.title}</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Suma puntos entregando tareas, rindiendo evaluaciones y aportando al Timeline
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-500">
                    {totalPuntos} PTS
                  </span>
                  <span className="block text-xs text-slate-400">
                    Próximo nivel: {levelInfo.next} pts
                  </span>
                </div>
              </div>
              <div className="w-full h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-700/50 mt-2">
                <div
                  className={`h-full bg-gradient-to-r ${levelInfo.color} rounded-full transition-all duration-500`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {/* Compositor del Día Integrado sin tapar contenido */}
          {randomComposer && (
            <div className="lg:col-span-4 flex flex-col">
              <div className="h-full bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-1 flex flex-col justify-center">
                <ComposerOfTheDay composer={randomComposer} onComposerClick={handleComposerClick} />
              </div>
            </div>
          )}
        </div>

        {/* Contenido para Estudiantes con Cátedras */}
        {(userType === 'student' || userType === 'both') && (
          <>
            {/* Tarjetas de Estadísticas Rápidas */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-emerald-500/30 p-5 hover:border-emerald-400/50 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm font-medium">Cátedras Inscritas</p>
                    <p className="text-white text-2xl sm:text-3xl font-bold mt-1">{catedras.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400">
                    <BookOpen size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-blue-500/30 p-5 hover:border-blue-400/50 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm font-medium">Tareas por Entregar</p>
                    <p className="text-white text-2xl sm:text-3xl font-bold mt-1">{tareasPendientes.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-400">
                    <FileText size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-amber-500/30 p-5 hover:border-amber-400/50 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm font-medium">Evaluaciones Pendientes</p>
                    <p className="text-white text-2xl sm:text-3xl font-bold mt-1">{evaluacionesPendientes.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center text-amber-400">
                    <CalendarCheck size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-purple-500/30 p-5 hover:border-purple-400/50 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm font-medium">Avisos Recientes</p>
                    <p className="text-white text-2xl sm:text-3xl font-bold mt-1">{publicaciones.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-purple-500/15 flex items-center justify-center text-purple-400">
                    <MessageSquare size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Centro de Acción Rápida: Tareas Pendientes y Evaluaciones Pendientes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Próximas Entregas de Tareas */}
              <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                      <Clock className="text-blue-400" size={22} />
                      Próximas Entregas de Tareas
                    </h2>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      {tareasPendientes.length} pendiente(s)
                    </span>
                  </div>

                  {tareasPendientes.length === 0 ? (
                    <div className="text-center py-8 bg-slate-800/30 rounded-xl border border-slate-700/40">
                      <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                      <p className="text-white font-medium">¡Estás al día con tus tareas!</p>
                      <p className="text-slate-400 text-sm mt-1">No tienes entregas pendientes por ahora.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {tareasPendientes.slice(0, 5).map((tarea) => {
                        const catedraId = tarea.TareaMaestra?.Catedra?.id;
                        const badge = getDeadlineBadge(tarea.TareaMaestra?.fecha_entrega, tarea.estado);
                        return (
                          <div
                            key={tarea.id}
                            className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 hover:border-blue-500/40 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                          >
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${badge.classes}`}>
                                  {badge.text}
                                </span>
                                <span className="text-xs text-slate-400 truncate">
                                  {tarea.TareaMaestra?.Catedra?.nombre || 'Cátedra'}
                                </span>
                              </div>
                              <h3 className="text-white font-semibold truncate">
                                {tarea.TareaMaestra?.titulo || 'Tarea asignada'}
                              </h3>
                              <p className="text-xs text-slate-400">
                                Valor: <strong className="text-slate-200">{tarea.TareaMaestra?.puntos_posibles || 0} pts</strong>
                                {tarea.TareaMaestra?.UnidadPlan?.periodo && ` • Unidad: ${tarea.TareaMaestra.UnidadPlan.periodo}`}
                              </p>
                            </div>

                            {catedraId && (
                              <button
                                onClick={() => navigate(`/alumno/catedra/${catedraId}`, {
                                  state: { activeTab: 'tareas', highlightId: tarea.id }
                                })}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium transition-colors flex-shrink-0 shadow-md shadow-blue-900/20"
                              >
                                <Upload size={15} />
                                Entregar ahora
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Últimas Tareas Calificadas con Devolución */}
                {tareasRecientesCalificadas.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-slate-800">
                    <h3 className="text-sm font-semibold text-emerald-300 mb-3 flex items-center gap-2">
                      <CheckCircle size={16} />
                      Últimas Calificaciones Recibidas
                    </h3>
                    <div className="space-y-2">
                      {tareasRecientesCalificadas.map((t) => (
                        <div
                          key={t.id}
                          onClick={() => t.TareaMaestra?.Catedra?.id && navigate(`/alumno/catedra/${t.TareaMaestra.Catedra.id}`, { state: { activeTab: 'tareas' } })}
                          className="cursor-pointer p-3 rounded-lg bg-emerald-950/30 border border-emerald-500/20 hover:border-emerald-500/40 flex items-center justify-between gap-2 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{t.TareaMaestra?.titulo}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {t.TareaMaestra?.Catedra?.nombre}
                              {t.comentario_docente && ` • 💬 "${t.comentario_docente}"`}
                            </p>
                          </div>
                          <span className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 font-bold text-xs flex-shrink-0">
                            {t.puntos_obtenidos} / {t.TareaMaestra?.puntos_posibles} pts
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Evaluaciones Pendientes y Novedades */}
              <div className="space-y-6">
                {/* Evaluaciones Pendientes */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                      <CalendarCheck className="text-amber-400" size={22} />
                      Evaluaciones por Resolver
                    </h2>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {evaluacionesPendientes.length} disponible(s)
                    </span>
                  </div>

                  {evaluacionesPendientes.length === 0 ? (
                    <div className="text-center py-6 bg-slate-800/30 rounded-xl border border-slate-700/40">
                      <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <p className="text-white font-medium text-sm">No tienes evaluaciones pendientes</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                      {evaluacionesPendientes.map((ev) => (
                        <div
                          key={ev.id}
                          className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/40 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                        >
                          <div className="space-y-1 min-w-0">
                            <span className="text-xs font-medium text-amber-300">
                              {ev.Catedra?.nombre || 'Cátedra'}
                            </span>
                            <h3 className="text-white font-semibold truncate">{ev.titulo}</h3>
                            {ev.UnidadPlan?.periodo && (
                              <p className="text-xs text-slate-400">
                                Módulo: {ev.UnidadPlan.periodo}
                              </p>
                            )}
                          </div>
                          <Link
                            to={`/evaluacion/${ev.id}`}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm font-medium transition-colors flex-shrink-0 shadow-md shadow-amber-900/20"
                          >
                            <Play size={15} />
                            Resolver
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Últimas Novedades del Tablón */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                      <MessageSquare className="text-purple-400" size={22} />
                      Últimos Anuncios del Tablón
                    </h2>
                  </div>

                  {publicaciones.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">
                      No hay anuncios recientes en tus cátedras.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                      {publicaciones.map((pub) => (
                        <Link
                          key={pub.id}
                          to={`/alumno/catedra/${pub.catedraId}`}
                          className="block p-3.5 rounded-xl bg-slate-800/40 hover:bg-slate-800/80 border border-slate-700/50 hover:border-purple-500/40 transition-all"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-xs font-semibold text-purple-300 truncate">
                              {pub.catedraNombre}
                            </span>
                            <span className="text-xs text-slate-500 flex-shrink-0">
                              {new Date(pub.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-white truncate">{pub.titulo}</p>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mis Cátedras */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2.5">
                  <BookMarked className="text-emerald-400" />
                  Mis Cátedras Inscritas
                </h2>
                <span className="text-sm text-slate-400">
                  Haz clic en una cátedra para acceder al Aula Virtual completa
                </span>
              </div>

              {catedras.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {catedras.map((inscripcion) => {
                    const cat = inscripcion.catedra || inscripcion.Catedra;
                    if (!cat || cat.id === undefined || cat.id === null) return null;

                    return (
                      <Link
                        key={cat.id}
                        to={`/alumno/catedra/${cat.id}`}
                        className="group flex flex-col justify-between bg-gradient-to-br from-emerald-900/20 via-slate-900/60 to-teal-900/20 backdrop-blur-sm rounded-2xl p-6 border border-emerald-500/30 hover:border-emerald-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-500/15"
                      >
                        <div>
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-900/30">
                              <BookOpen className="text-white" size={24} />
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                              Año {cat.anio}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-300 transition-colors">
                            {cat.nombre}
                          </h3>

                          <div className="space-y-1.5 text-sm text-slate-300 mt-3">
                            <div className="flex items-center gap-2 text-slate-400">
                              <Home size={15} className="text-emerald-400 flex-shrink-0" />
                              <span className="truncate">{cat.institucion || 'Institución no especificada'}</span>
                            </div>
                            {cat.dias && (
                              <div className="flex items-center gap-2 text-slate-400">
                                <Calendar size={15} className="text-teal-400 flex-shrink-0" />
                                <span className="truncate">{cat.dias} {cat.turno ? `(${cat.turno})` : ''}</span>
                              </div>
                            )}
                            {cat.aula && (
                              <div className="flex items-center gap-2 text-slate-400">
                                <MapPin size={15} className="text-purple-400 flex-shrink-0" />
                                <span>Aula: {cat.aula}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-5 pt-4 border-t border-slate-700/50 flex items-center justify-between text-emerald-400 text-sm font-medium">
                          <span>Entrar al Aula Virtual</span>
                          <ChevronRight className="group-hover:translate-x-1 transition-transform" size={18} />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No estás inscrito en ninguna cátedra</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Usuario Nuevo sin Cátedras */}
        {userType === 'new' && (
          <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Sparkles className="text-white" size={40} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">¡Bienvenido a EduPlatForm!</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Comienza tu viaje musical contribuyendo al Timeline de compositores o consultando las cátedras disponibles en la plataforma.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-xl transition-all"
              >
                Explorar Timeline
              </Link>
              <Link
                to="/public/catedras"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-xl border border-slate-600 transition-all"
              >
                Ver Cátedras Públicas
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumnoDashboardPage;
